// Admin-Page — Owner-only. Sub-Tabs: User-Liste, Credits (Feature-Preise +
// Credit-Pakete + Abo-Pläne), Globale RAS, Cron-Status, Audit-Log.
// Source-of-truth: mockups/05-admin/proposal.html + approved.md.
//
// Hinweis: Das frühere Tier-Matrix/Tier-Limits-Panel ist abgelöst durch das
// Credits-Modell (Abo-Pläne + Credit-Pakete). Die Tier-Components existieren
// noch dormant (tier-matrix.tsx / tier-limits.tsx), sind aber nicht mehr aus
// der UI erreichbar.

import { useState } from 'react';
import { Shield } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { AdminAuditLog } from '@/components/admin/admin-audit-log';
import { AdminCronStatus } from '@/components/admin/admin-cron-status';
import { AdminGlobalRas } from '@/components/admin/admin-global-ras';
import { AdminUsers } from '@/components/admin/admin-users';
import { FeatureCostEditor } from '@/components/admin/credits/feature-cost-editor';
import { CreditPackEditor } from '@/components/admin/credits/credit-pack-editor';
import { PlanEditor } from '@/components/admin/subscriptions/plan-editor';
import { KnowledgeTab } from '@/components/admin/knowledge/knowledge-tab';
import { PlaybooksTab } from '@/components/admin/playbooks/playbooks-tab';

interface SubTab {
  id: string;
  label: string;
  comingSoon?: boolean;
}

const SUB_TABS: ReadonlyArray<SubTab> = [
  { id: 'users', label: 'User-Liste' },
  { id: 'credits', label: 'Credits' },
  { id: 'knowledge', label: 'Wissen' },
  { id: 'playbooks', label: 'Playbooks' },
  { id: 'ras', label: 'Globale RAS' },
  { id: 'cron', label: 'Cron-Status' },
  { id: 'audit', label: 'Audit-Log' },
];

export function AdminPage() {
  const { isOwner, profile } = useAuth();
  const [activeTab, setActiveTab] = useState<string>('users');

  // Non-Owner werden hart auf /markt umgeleitet (Sidebar-Eintrag fehlt eh,
  // aber Direct-URL-Navigation muss blockiert sein). Hooks darüber müssen
  // stabil bleiben (Rules-of-Hooks).
  if (!isOwner) {
    return <Navigate to="/markt" replace />;
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header + Sub-Tabs */}
      <div className="shrink-0 border-b border-white/5">
        <div className="flex items-center gap-3 px-5 pt-4 pb-2">
          <Shield className="size-5 text-rose-400" />
          <h1 className="text-xl font-semibold">Admin</h1>
          <span className="rounded-full border border-rose-500/20 bg-rose-500/15 px-2 py-0.5 text-[11px] text-rose-300">
            Owner-only
          </span>
          <div className="flex-1" />
          <span className="text-[12px] text-neutral-500">
            Eingeloggt als <span className="text-amber-300">{profile?.email ?? '—'}</span>
          </span>
        </div>
        <div className="flex items-center gap-1 px-5">
          {SUB_TABS.map((tab) => {
            const active = tab.id === activeTab;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => !tab.comingSoon && setActiveTab(tab.id)}
                className={[
                  'border-b-2 px-3 py-2 text-[13px] transition',
                  active
                    ? 'border-amber-500 font-medium text-amber-300'
                    : 'border-transparent text-neutral-500 hover:text-neutral-300',
                  tab.comingSoon ? 'cursor-not-allowed' : '',
                ].join(' ')}
                disabled={tab.comingSoon}
              >
                {tab.label}
                {tab.comingSoon ? (
                  <span className="ml-1 text-[11px] opacity-60">(später)</span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {/* Inhalt */}
      <div className="scroll-hidden flex-1 space-y-5 overflow-y-auto p-5">
        {activeTab === 'users' ? (
          <AdminUsers />
        ) : activeTab === 'credits' ? (
          <>
            <FeatureCostEditor />
            <CreditPackEditor />
            <PlanEditor />
          </>
        ) : activeTab === 'knowledge' ? (
          <KnowledgeTab />
        ) : activeTab === 'playbooks' ? (
          <PlaybooksTab />
        ) : activeTab === 'ras' ? (
          <AdminGlobalRas />
        ) : activeTab === 'cron' ? (
          <AdminCronStatus />
        ) : activeTab === 'audit' ? (
          <AdminAuditLog />
        ) : null}
      </div>
    </div>
  );
}
